import React from 'react';
import { UserCircle, Loader } from 'lucide-react';
import { faRobot } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { v4 as uuidv4 } from 'uuid';

interface ChatMessage {
  id: string;
  chat_id: string;
  content: string;
  sender: string;
  app_id: string;
}

interface ChatThread {
  id: string;
  user_name: string;
  title: string;
  last_updated: string;
  messages: ChatMessage[];
  isExpanded: boolean;
}

interface ChatMessagesListProps {
  thread: ChatThread;
  loadingThreadId: string | null;
  safeAiIconSrc: string;
  assistant: any;
}

const ChatMessagesList: React.FC<ChatMessagesListProps> = ({
  thread,
  loadingThreadId,
  safeAiIconSrc,
  assistant,
}) => {
  const formatMessageContent = (content: string) => {
    return content
      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>") // Convert **bold**
      .replace(/(?<!\*)\*(?!\*)(.*?)\*(?!\*)/g, "<em>$1</em>") // Convert *italic* only if not bold
      .replace(/###\s*(.*?)(?=\n|$)/g, "<h3>$1</h3>") // Convert ### headings   
      .replace(/\n/g, "<br>") // Convert new lines to <br>
      .replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" style="color: inherit; text-decoration: underline;">$1</a>'); // Convert links
  };

  const renderMessageIcon = (sender: string) => {
    if (sender === "user") {
      return <UserCircle className="h-5 w-5 text-gray-600" />;
    } else {
      if (assistant?.app_ai_face) {
        return (
          <img
            src={safeAiIconSrc}
            alt="AI Face"
            className="h-5 cursor-pointer"
          />
        );
      } else {
        return <FontAwesomeIcon icon={faRobot} className="h-8 w-8 text-black" />;
      }
    }
  };

  if (loadingThreadId === thread.id) {
    return (
      <div className="space-y-2 max-h-60 overflow-y-auto pr-2">
        <Loader className="animate-spin text-yellow-500 w-6 h-6" />
      </div>
    );
  }

  return (
    <div className="space-y-2 max-h-60 overflow-y-auto pr-2">
      {(thread.messages || []).map((message) => (
        <div 
          key={uuidv4()} 
          className={`flex w-full ${
            message.sender === "user" ? "justify-end" : "justify-start"
          }`}
        >
          <div className="max-w-[80%] p-2 rounded-lg text-sm break-words whitespace-pre-wrap bg-gray-100">
            <div className="flex items-start gap-2">
              <div className="shrink-0">
                {renderMessageIcon(message.sender)}
              </div>
              <div className="text-sm break-words whitespace-pre-wrap text-left"> 
                <span 
                  dangerouslySetInnerHTML={{ 
                    __html: formatMessageContent(message.content)
                  }} 
                />
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ChatMessagesList;